/**
 * Sound effects (SPEC Section 7 screen 5: "Sounds are optional in Phase 1;
 * leave hooks"). The hooks were wired in step 8; this fills them in the same
 * way the art was filled: placeholder first, real asset is a file drop (D-088).
 *
 *   play(tag, { pose })
 *
 * `tag` is the animal record's `sound` field ("roar", "hiss", "buzz"…), the
 * same tag the Phase 2 audio box keys its effects off. If public/sounds/{tag}.mp3
 * exists (roster.json lists what is there at build time, see indexSounds) it is
 * played. Otherwise a short synthesized sound in the tag's family plays instead:
 * a rumble for a roar, filtered noise for a hiss, a sweep for a howl, blips for
 * a chirp. No files, no network, nothing a toddler can break — and every sound
 * respects the one mute switch in the top bar.
 *
 * iOS only lets audio start inside a user gesture, so the AudioContext is
 * created and resumed on the first tap anywhere; the beat effects that follow
 * (fired from React effects, outside the gesture) then work.
 */

import { isMuted } from './speech.js'

/* ---------------- real files ---------------- */

let files = new Set()
const audioPool = new Map()

/** Called once with roster.json's `sounds` list: tags that have a real file. */
export function indexSounds(list = []) {
  files = new Set(list)
}

function playFile(tag, pose) {
  let el = audioPool.get(tag)
  if (!el) {
    el = new Audio(`/sounds/${tag}.mp3`)
    el.preload = 'auto'
    audioPool.set(tag, el)
  }
  el.volume = pose === 'retreating' ? 0.45 : 0.8
  el.currentTime = 0
  el.play().catch(() => {
    /* not allowed yet, or the file is bad — silence is fine */
  })
}

/* ---------------- synthesized placeholders ---------------- */

let ctx = null
function unlock() {
  try {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)()
    if (ctx.state === 'suspended') ctx.resume()
  } catch {
    ctx = null
  }
}
if (typeof window !== 'undefined') {
  for (const ev of ['pointerdown', 'touchend', 'keydown']) window.addEventListener(ev, unlock, { passive: true, capture: true })
}

/**
 * The page's one AudioContext, once a tap has made it, or null. The arena's
 * sounds (D-195) play through it too: a context made anywhere else, outside a
 * tap, would stay silent on iOS.
 */
export function audioContext() {
  return ctx && ctx.state !== 'closed' ? ctx : null
}

/** Tag → sound family. Anything unmatched is a chirp. */
const FAMILIES = [
  [/roar|bellow|rumble|boom|moan|bugle|trumpet|bray|moo|honk|neigh/, 'roar'],
  [/growl|grumble|grunt|snort|snuffle|chuff|oink|huff|rasp|purr/, 'growl'],
  [/hiss|rattle|rustle|skitter|hush|rush|splash|bubble|whir|buzz|hum|flutter/, 'hiss'],
  [/howl|song|hoot|whoop|yowl|scream|screech|croak|meow/, 'howl'],
  [/thump|gulp|zap|snap|click|pop|tick/, 'thump'],
]
export function familyOf(tag = '') {
  return FAMILIES.find(([re]) => re.test(tag))?.[1] || 'chirp'
}

/** Pose shapes the sound: victorious rings out, retreating trails off, fierce bites harder. */
const POSE = {
  neutral: { pitch: 1, dur: 1, gain: 1 },
  fierce: { pitch: 1.05, dur: 1, gain: 1.25 },
  victorious: { pitch: 1.15, dur: 1.35, gain: 1.1 },
  retreating: { pitch: 0.85, dur: 0.9, gain: 0.55 },
}

function envelope(node, t0, dur, peak) {
  const g = ctx.createGain()
  g.gain.setValueAtTime(0.0001, t0)
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.02)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  node.connect(g)
  g.connect(ctx.destination)
  return g
}

function tone({ type = 'sine', f0, f1 = f0, t0, dur, gain, vibrato = 0 }) {
  const o = ctx.createOscillator()
  o.type = type
  o.frequency.setValueAtTime(f0, t0)
  o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur)
  if (vibrato) {
    const lfo = ctx.createOscillator()
    const depth = ctx.createGain()
    lfo.frequency.value = 6
    depth.gain.value = vibrato
    lfo.connect(depth)
    depth.connect(o.frequency)
    lfo.start(t0)
    lfo.stop(t0 + dur)
  }
  envelope(o, t0, dur, gain)
  o.start(t0)
  o.stop(t0 + dur + 0.05)
}

function noise({ t0, dur, gain, type = 'lowpass', f0, f1 = f0, q = 1 }) {
  const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * dur), ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
  const src = ctx.createBufferSource()
  src.buffer = buffer
  const filter = ctx.createBiquadFilter()
  filter.type = type
  filter.Q.value = q
  filter.frequency.setValueAtTime(f0, t0)
  filter.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur)
  src.connect(filter)
  envelope(filter, t0, dur, gain)
  src.start(t0)
  src.stop(t0 + dur + 0.05)
}

const SYNTH = {
  roar: (t0, p) => {
    noise({ t0, dur: 0.6 * p.dur, gain: 0.22 * p.gain, f0: 420 * p.pitch, f1: 140 * p.pitch })
    tone({ type: 'sawtooth', f0: 110 * p.pitch, f1: 65 * p.pitch, t0, dur: 0.6 * p.dur, gain: 0.1 * p.gain })
  },
  growl: (t0, p) => {
    tone({ type: 'sawtooth', f0: 95 * p.pitch, f1: 80 * p.pitch, t0, dur: 0.45 * p.dur, gain: 0.12 * p.gain, vibrato: 12 })
    noise({ t0, dur: 0.45 * p.dur, gain: 0.07 * p.gain, f0: 320 * p.pitch, f1: 200 * p.pitch })
  },
  hiss: (t0, p) => {
    noise({ t0, dur: 0.5 * p.dur, gain: 0.11 * p.gain, type: 'bandpass', f0: 2800 * p.pitch, f1: 5200 * p.pitch, q: 0.8 })
  },
  howl: (t0, p) => {
    tone({ type: 'sine', f0: 380 * p.pitch, f1: 640 * p.pitch, t0, dur: 0.35 * p.dur, gain: 0.14 * p.gain, vibrato: 18 })
    tone({ type: 'sine', f0: 640 * p.pitch, f1: 460 * p.pitch, t0: t0 + 0.33 * p.dur, dur: 0.4 * p.dur, gain: 0.12 * p.gain, vibrato: 18 })
  },
  thump: (t0, p) => {
    tone({ type: 'sine', f0: 130 * p.pitch, f1: 38, t0, dur: 0.2 * p.dur, gain: 0.3 * p.gain })
    noise({ t0, dur: 0.07, gain: 0.12 * p.gain, f0: 1800, f1: 600 })
  },
  chirp: (t0, p) => {
    const n = p.dur > 1.2 ? 3 : 2
    for (let i = 0; i < n; i++) {
      tone({ type: 'sine', f0: 1150 * p.pitch, f1: 1750 * p.pitch, t0: t0 + i * 0.15, dur: 0.09, gain: 0.11 * p.gain })
    }
  },
}

let lastAt = 0

/**
 * Play the effect for an animal's `sound` tag. Silent when muted, when audio has
 * not been unlocked by a tap yet, or if another effect started under 120ms ago
 * (a two-year-old's mashing must not become a wall of noise).
 */
export function play(tag, { pose = 'neutral' } = {}) {
  if (!tag || isMuted()) return
  const now = Date.now()
  if (now - lastAt < 120) return
  lastAt = now
  if (files.has(tag)) return playFile(tag, pose)
  if (!ctx || ctx.state !== 'running') return
  try {
    const p = POSE[pose] || POSE.neutral
    SYNTH[familyOf(tag)](ctx.currentTime, p)
  } catch {
    /* an audio hiccup is never worth an error */
  }
}

export default { play, indexSounds, familyOf, audioContext }
